import type {
  AttentionProjection,
  BoardEditableField,
  BoardItemProjection,
  BoardStatus,
  Capability,
  CapabilityProviderProjection,
  CrewProjection,
  DecisionProjection,
  EvidenceProjection,
  ForgeRemoteProjection,
  ManagerMessageProjection,
  ManagerTranscriptProjection,
  MemberGraphPlacementProjection,
  MemberProjection,
  RemoteGraphProjection,
  RemoteResourceProjection,
  RemoteSystemProjection,
  RunPlanStep,
  RunProjection,
  SessionProjection,
  WorkItemReference,
} from "../domain";
import type { ActivationPreview, ActivationRedemption, ProjectionEnvelope } from "./contracts";

const permissionPresets = new Set<SessionProjection["preset"]>(["observe", "converse", "operate", "custom"]);
const capabilities = new Set<Capability>([
  "projection.read", "evidence.read", "manager.chat", "attention.ack",
  "attention.snooze", "board.edit", "decision.respond", "run.pause", "run.resume",
]);
const gatewayStates = new Set<SessionProjection["gateway"]["state"]>(["connected", "reconnecting", "offline"]);
const hostStates = new Set<SessionProjection["host"]["state"]>(["reachable", "unavailable"]);
const projectionStates = new Set<SessionProjection["projection"]["state"]>(["live", "delayed", "stale"]);
const commandAuthorityStates = new Set<SessionProjection["commandAuthority"]["state"]>(["available", "unavailable"]);
const runStates = new Set<RunProjection["state"]>(["idle", "planning", "running", "waiting", "paused", "completed", "failed", "unknown"]);
const crewHealthStates = new Set<CrewProjection["health"]>(["healthy", "needs-input", "at-risk", "completed", "failed", "unknown"]);
const memberHealthStates = new Set<MemberProjection["health"]>(["online", "busy", "waiting", "stale", "offline", "unregistered", "unknown"]);
const attentionKinds = new Set<AttentionProjection["kind"]>(["decision", "risk", "running", "completed", "blocker", "info", "command-failed"]);
const attentionObjectTypes = new Set<AttentionProjection["objectType"]>(["board-item", "decision", "run", "goal", "intervention"]);
const boardStatuses = new Set<BoardStatus>(["backlog", "next", "in-progress", "blocked", "review", "done"]);
const boardPriorities = new Set<BoardItemProjection["priority"]>(["urgent", "high", "normal", "low"]);
const boardEditableFields = new Set<BoardEditableField>(["status", "priority", "assignee", "checkpoint", "operatorNote"]);
const runActions = new Set<RunProjection["allowedActions"][number]>(["pause", "resume"]);
const runPlanStates = new Set<RunPlanStep["state"]>(["done", "current", "pending"]);
const managerAuthors = new Set<ManagerMessageProjection["authorType"]>(["manager", "member", "operator", "system"]);
const messageDeliveries = new Set<NonNullable<ManagerMessageProjection["delivery"]>>(["queued", "requested", "accepted", "delivered", "failed"]);
const managerTranscriptStatuses = new Set<ManagerTranscriptProjection["status"]>(["available", "unavailable"]);
const decisionStates = new Set<DecisionProjection["state"]>(["waiting", "approved", "held"]);
const systemStates = new Set<RemoteSystemProjection["state"]>(["reachable", "degraded", "offline", "unknown"]);
const graphStates = new Set<RemoteGraphProjection["state"]>(["online", "degraded", "offline", "unknown"]);
const resourceTypes = new Set<RemoteResourceProjection["resourceType"]>(["git-repository", "git-branch", "workspace", "browser", "other"]);
const providerCapabilities = new Set([
  "observe-repository", "analyze-repository", "analyze-contributors", "read-diff",
  "plan-commit", "apply-commit", "plan-push", "apply-push", "unknown",
]);
export class PortalApiError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
    this.name = "PortalApiError";
  }
}

function contractError(label: string): never {
  throw new PortalApiError(`${label} did not match the Forge Remote contract.`);
}

function record(value: unknown, label: string): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) contractError(label);
  return value as Record<string, unknown>;
}

function textValue(value: unknown, label: string): string {
  if (typeof value !== "string") contractError(label);
  return value;
}

function identifier(value: unknown, label: string): string {
  const candidate = textValue(value, label);
  if (candidate.length === 0) contractError(label);
  return candidate;
}

function canonicalCrewId(value: unknown, label: string): string {
  const id = identifier(value, label);
  const match = /^crew_[0-9a-f]{32}$/.exec(id);
  if (!match || match[0].length !== id.length) contractError(label);
  return id;
}

function canonicalBoardId(value: unknown, label: string): string {
  const id = identifier(value, label);
  const match = /^board_[0-9a-f]{32}$/.exec(id);
  if (!match || match[0].length !== id.length) contractError(label);
  return id;
}

function canonicalWorkId(value: unknown, label: string): string {
  const id = identifier(value, label);
  const match = /^[a-z0-9](?:[a-z0-9._-]*[a-z0-9])?$/.exec(id);
  if (id.length > 128 || !match || match[0].length !== id.length) contractError(label);
  return id;
}

function mapWorkItem(value: unknown, label: string): WorkItemReference {
  const workItem = record(value, label);
  return {
    crewId: canonicalCrewId(workItem.crewId, `${label} Crew ID`),
    boardId: canonicalBoardId(workItem.boardId, `${label} Board ID`),
    cardId: canonicalWorkId(workItem.cardId, `${label} Card ID`),
    goalId: canonicalWorkId(workItem.goalId, `${label} Goal ID`),
  };
}

function optionalWorkItem(value: unknown, label: string): WorkItemReference | undefined {
  return value === null || value === undefined ? undefined : mapWorkItem(value, label);
}

function timestamp(value: unknown, label: string): string {
  const candidate = identifier(value, label);
  if (!Number.isFinite(Date.parse(candidate))) contractError(label);
  return candidate;
}

function integer(value: unknown, label: string, minimum = Number.MIN_SAFE_INTEGER): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < minimum) contractError(label);
  return value;
}

function percentage(value: unknown, label: string): number {
  const candidate = integer(value, label, 0);
  if (candidate > 100) contractError(label);
  return candidate;
}

function booleanValue(value: unknown, label: string): boolean {
  if (typeof value !== "boolean") contractError(label);
  return value;
}

function enumValue<T extends string>(value: unknown, allowed: ReadonlySet<T>, label: string): T {
  const candidate = identifier(value, label);
  if (!allowed.has(candidate as T)) throw new PortalApiError(`${label} contained an unsupported value.`);
  return candidate as T;
}

function arrayValue<T>(value: unknown, label: string, map: (item: unknown, label: string) => T): T[] {
  if (!Array.isArray(value)) contractError(label);
  return value.map((item, index) => map(item, `${label}[${index}]`));
}

function identifierArray(value: unknown, label: string): string[] {
  const result = arrayValue(value, label, identifier);
  if (new Set(result).size !== result.length) throw new PortalApiError(`${label} contained duplicate values.`);
  return result;
}

function enumArray<T extends string>(value: unknown, allowed: ReadonlySet<T>, label: string): T[] {
  const result = arrayValue(value, label, (item, itemLabel) => enumValue(item, allowed, itemLabel));
  if (new Set(result).size !== result.length) throw new PortalApiError(`${label} contained duplicate values.`);
  return result;
}

function nullableIdentifier(value: unknown, label: string): string | null {
  if (value === null) return null;
  return identifier(value, label);
}

function optionalIdentifier(value: unknown, label: string): string | undefined {
  return nullableIdentifier(value, label) ?? undefined;
}

function mapSession(value: unknown): SessionProjection {
  const session = record(value, "Projection session");
  const gateway = record(session.gateway, "Projection gateway");
  const host = record(session.host, "Projection host");
  const projection = record(session.projection, "Projection freshness");
  const commandAuthority = record(session.commandAuthority, "Projection command authority");
  return {
    sessionId: identifier(session.sessionId, "Session ID"),
    deviceLabel: identifier(session.deviceLabel, "Session device label"),
    hostLabel: identifier(session.hostLabel, "Session host label"),
    preset: enumValue(session.preset, permissionPresets, "Permission preset"),
    capabilities: enumArray(session.capabilities, capabilities, "Session capabilities"),
    expiresAt: timestamp(session.expiresAt, "Session expiry"),
    gateway: {
      state: enumValue(gateway.state, gatewayStates, "Gateway state"),
      lastConnectedAt: timestamp(gateway.lastConnectedAt, "Gateway connection time"),
    },
    host: {
      state: enumValue(host.state, hostStates, "Forge host state"),
      lastSeenAt: timestamp(host.lastSeenAt, "Forge host observation time"),
    },
    projection: {
      state: enumValue(projection.state, projectionStates, "Projection state"),
      snapshotAt: timestamp(projection.snapshotAt, "Projection snapshot time"),
      sourceEventAt: timestamp(projection.sourceEventAt, "Projection source event time"),
      cursor: identifier(projection.cursor, "Projection state cursor"),
    },
    commandAuthority: {
      state: enumValue(commandAuthority.state, commandAuthorityStates, "Command authority state"),
      reason: textValue(commandAuthority.reason, "Command authority reason"),
    },
  };
}

function mapMember(value: unknown, label: string): MemberProjection {
  const member = record(value, label);
  return {
    id: identifier(member.id, `${label} ID`),
    name: identifier(member.name, `${label} name`),
    role: textValue(member.role, `${label} role`),
    health: enumValue(member.health, memberHealthStates, `${label} health`),
    currentWork: textValue(member.currentWork, `${label} current work`),
    lastBeatAt: timestamp(member.lastBeatAt, `${label} last beat`),
  };
}

function mapRun(value: unknown, label: string): RunProjection {
  const run = record(value, label);
  return {
    id: identifier(run.id, `${label} ID`),
    workItem: mapWorkItem(run.workItem, `${label} WorkItem`),
    title: identifier(run.title, `${label} title`),
    revision: integer(run.revision, `${label} revision`, 0),
    state: enumValue(run.state, runStates, `${label} state`),
    progress: percentage(run.progress, `${label} progress`),
    nextCheckpoint: textValue(run.nextCheckpoint, `${label} next checkpoint`),
    allowedActions: enumArray(run.allowedActions, runActions, `${label} allowed actions`),
    plan: arrayValue(run.plan, `${label} plan`, (item, itemLabel) => {
      const step = record(item, itemLabel);
      return {
        id: identifier(step.id, `${itemLabel} ID`),
        title: identifier(step.title, `${itemLabel} title`),
        state: enumValue(step.state, runPlanStates, `${itemLabel} state`),
      };
    }),
  };
}

function mapBoardItem(value: unknown, label: string): BoardItemProjection {
  const item = record(value, label);
  const checkpointAt = item.checkpointAt === null ? null : timestamp(item.checkpointAt, `${label} checkpoint`);
  return {
    id: identifier(item.id, `${label} ID`),
    crewId: canonicalCrewId(item.crewId, `${label} Crew ID`),
    boardId: canonicalBoardId(item.boardId, `${label} Board ID`),
    cardId: canonicalWorkId(item.cardId, `${label} Card ID`),
    goalId: canonicalWorkId(item.goalId, `${label} Goal ID`),
    revision: integer(item.revision, `${label} revision`, 0),
    title: identifier(item.title, `${label} title`),
    summary: textValue(item.summary, `${label} summary`),
    status: enumValue(item.status, boardStatuses, `${label} status`),
    priority: enumValue(item.priority, boardPriorities, `${label} priority`),
    assigneeId: nullableIdentifier(item.assigneeId, `${label} assignee`),
    checkpointAt,
    operatorNote: textValue(item.operatorNote, `${label} operator note`),
    remoteEditableFields: enumArray(item.remoteEditableFields, boardEditableFields, `${label} editable fields`),
    allowedStatusTransitions: enumArray(item.allowedStatusTransitions, boardStatuses, `${label} status transitions`),
  };
}

function mapManagerMessage(value: unknown, label: string): ManagerMessageProjection {
  const message = record(value, label);
  return {
    id: identifier(message.id, `${label} ID`),
    authorType: enumValue(message.authorType, managerAuthors, `${label} author type`),
    authorLabel: identifier(message.authorLabel, `${label} author label`),
    createdAt: timestamp(message.createdAt, `${label} creation time`),
    text: textValue(message.text, `${label} text`),
    reference: optionalIdentifier(message.reference, `${label} reference`),
    delivery: enumValue(message.delivery, messageDeliveries, `${label} delivery`),
  };
}

function mapManagerTranscript(value: unknown, label: string): ManagerTranscriptProjection {
  const transcript = record(value, label);
  const status = enumValue(transcript.status, managerTranscriptStatuses, `${label} status`);
  const cursor = identifier(transcript.cursor, `${label} cursor`);
  const messages = arrayValue(transcript.messages, `${label} messages`, mapManagerMessage);
  const isUnavailableCursor = cursor === "manager-transcript-unavailable";
  if ((status === "unavailable") !== isUnavailableCursor) {
    throw new PortalApiError(`${label} status and cursor disagree.`);
  }
  if (status === "unavailable" && messages.length > 0) {
    throw new PortalApiError(`${label} cannot contain messages while unavailable.`);
  }
  return {
    status,
    cursor,
    revision: integer(transcript.revision, `${label} revision`, 0),
    historyTruncated: booleanValue(transcript.historyTruncated, `${label} history marker`),
    messages,
  };
}

function mapDecision(value: unknown, label: string): DecisionProjection {
  const decision = record(value, label);
  return {
    id: identifier(decision.id, `${label} ID`),
    workItem: optionalWorkItem(decision.workItem, `${label} WorkItem`),
    revision: integer(decision.revision, `${label} revision`, 0),
    title: identifier(decision.title, `${label} title`),
    consequence: textValue(decision.consequence, `${label} consequence`),
    requestedBy: identifier(decision.requestedBy, `${label} requester`),
    requestedAt: timestamp(decision.requestedAt, `${label} request time`),
    runId: textValue(decision.runId, `${label} Run ID`),
    evidenceIds: identifierArray(decision.evidenceIds, `${label} evidence IDs`),
    state: enumValue(decision.state, decisionStates, `${label} state`),
  };
}

function mapEvidence(value: unknown, label: string): EvidenceProjection {
  const evidence = record(value, label);
  return {
    id: identifier(evidence.id, `${label} ID`),
    occurredAt: timestamp(evidence.occurredAt, `${label} occurrence time`),
    actor: identifier(evidence.actor, `${label} actor`),
    finding: textValue(evidence.finding, `${label} finding`),
    desktopOnly: booleanValue(evidence.desktopOnly, `${label} desktop marker`),
  };
}

function mapSystem(value: unknown, label: string): RemoteSystemProjection {
  const system = record(value, label);
  if (system.kind !== "remote-system") contractError(`${label} kind`);
  return {
    id: identifier(system.id, `${label} ID`),
    label: identifier(system.label, `${label} label`),
    kind: "remote-system",
    state: enumValue(system.state, systemStates, `${label} state`),
  };
}

function mapProvider(value: unknown, label: string): CapabilityProviderProjection {
  const provider = record(value, label);
  return {
    id: identifier(provider.id, `${label} ID`),
    label: identifier(provider.label, `${label} label`),
    providerType: identifier(provider.providerType, `${label} type`),
    capabilities: enumArray(provider.capabilities, providerCapabilities, `${label} capabilities`),
    systemId: optionalIdentifier(provider.systemId, `${label} system ID`),
  };
}

function mapGraph(value: unknown, label: string): RemoteGraphProjection {
  const graph = record(value, label);
  return {
    id: identifier(graph.id, `${label} ID`),
    label: identifier(graph.label, `${label} label`),
    graphType: identifier(graph.graphType, `${label} type`),
    state: enumValue(graph.state, graphStates, `${label} state`),
    systemId: identifier(graph.systemId, `${label} system ID`),
    capabilityProviderIds: identifierArray(graph.capabilityProviderIds, `${label} capability-provider IDs`),
    resourceIds: identifierArray(graph.resourceIds, `${label} resource IDs`),
  };
}

function mapResource(value: unknown, label: string): RemoteResourceProjection {
  const resource = record(value, label);
  return {
    id: identifier(resource.id, `${label} ID`),
    label: identifier(resource.label, `${label} label`),
    resourceType: enumValue(resource.resourceType, resourceTypes, `${label} type`),
    repositoryId: optionalIdentifier(resource.repositoryId, `${label} repository ID`),
    branchName: optionalIdentifier(resource.branchName, `${label} branch name`),
    systemId: identifier(resource.systemId, `${label} system ID`),
  };
}

function mapPlacement(value: unknown, label: string): MemberGraphPlacementProjection {
  const placement = record(value, label);
  return {
    memberId: identifier(placement.memberId, `${label} member ID`),
    graphId: identifier(placement.graphId, `${label} graph ID`),
    placementRole: textValue(placement.placementRole, `${label} role`),
  };
}

function mapCrew(value: unknown, label: string, canReadEvidence: boolean): CrewProjection {
  const crew = record(value, label);
  const crewId = canonicalCrewId(crew.id, `${label} ID`);
  const boardId = canonicalBoardId(crew.boardId, `${label} Board ID`);
  const boardRevision = integer(crew.boardRevision, `${label} Board revision`, 0);
  const since = record(crew.since, `${label} since-last-seen`);
  textValue(since.fromCursor, `${label} since-last-seen cursor`);
  const sinceSummary = textValue(since.summary, `${label} since-last-seen summary`);
  const evidence = arrayValue(crew.evidence, `${label} evidence`, mapEvidence);
  const board = arrayValue(crew.board, `${label} Board`, mapBoardItem);
  const runs = arrayValue(crew.runs, `${label} Runs`, mapRun);
  const decisions = arrayValue(crew.decisions, `${label} decisions`, mapDecision);
  if (!canReadEvidence && evidence.length > 0) {
    throw new PortalApiError("Projection included evidence without evidence.read authority.");
  }
  if (board.some((item) => item.crewId !== crewId
      || item.boardId !== boardId
      || item.revision !== boardRevision
      || item.id !== item.cardId)
      || new Set(board.map((item) => item.cardId)).size !== board.length) {
    throw new PortalApiError(`${label} Board did not preserve its Crew/Board/Card identity.`);
  }
  const boardByCard = new Map(board.map((item) => [item.cardId, item]));
  const containsWorkItem = (workItem: WorkItemReference) => {
    const item = boardByCard.get(workItem.cardId);
    return workItem.crewId === crewId
      && workItem.boardId === boardId
      && item?.goalId === workItem.goalId;
  };
  if (runs.some((run) => !containsWorkItem(run.workItem))
      || decisions.some((decision) => decision.workItem && !containsWorkItem(decision.workItem))) {
    throw new PortalApiError(`${label} Run or decision escaped its exact Crew Board WorkItem identity.`);
  }
  return {
    id: crewId,
    name: identifier(crew.name, `${label} name`),
    shortName: identifier(crew.shortName, `${label} short name`),
    revision: integer(crew.revision, `${label} revision`, 0),
    boardId,
    boardRevision,
    runState: enumValue(crew.runState, runStates, `${label} Run state`),
    health: enumValue(crew.health, crewHealthStates, `${label} health`),
    summary: textValue(crew.summary, `${label} summary`),
    since: {
      from: timestamp(since.from, `${label} since-last-seen time`),
      countsAvailable: booleanValue(since.countsAvailable, `${label} count availability`),
      eventCount: integer(since.eventCount, `${label} event count`, 0),
      completedCount: integer(since.completedCount, `${label} completed count`, 0),
      decisionCount: integer(since.decisionCount, `${label} decision count`, 0),
      summary: sinceSummary,
    },
    members: arrayValue(crew.members, `${label} members`, mapMember),
    runs,
    board,
    manager: mapManagerTranscript(crew.manager, `${label} Manager transcript`),
    decisions,
    evidence,
    systems: arrayValue(crew.systems, `${label} systems`, mapSystem),
    capabilityProviders: arrayValue(crew.capabilityProviders, `${label} capability providers`, mapProvider),
    graphs: arrayValue(crew.graphs, `${label} graphs`, mapGraph),
    resources: arrayValue(crew.resources, `${label} resources`, mapResource),
    memberGraphPlacements: arrayValue(crew.memberGraphPlacements, `${label} member graph placements`, mapPlacement),
  };
}

function mapAttention(value: unknown, label: string): AttentionProjection {
  const item = record(value, label);
  return {
    id: identifier(item.id, `${label} ID`),
    crewId: canonicalCrewId(item.crewId, `${label} Crew ID`),
    workItem: optionalWorkItem(item.workItem, `${label} WorkItem`),
    kind: enumValue(item.kind, attentionKinds, `${label} kind`),
    rank: integer(item.rank, `${label} rank`),
    title: identifier(item.title, `${label} title`),
    summary: textValue(item.summary, `${label} summary`),
    occurredAt: timestamp(item.occurredAt, `${label} occurrence time`),
    actor: identifier(item.actor, `${label} actor`),
    objectType: enumValue(item.objectType, attentionObjectTypes, `${label} object type`),
    objectId: identifier(item.objectId, `${label} object ID`),
    acknowledged: booleanValue(item.acknowledged, `${label} acknowledgement`),
    isNew: booleanValue(item.isNew, `${label} new marker`),
  };
}

function mapProjection(value: unknown): ForgeRemoteProjection {
  const projection = record(value, "Projection");
  const session = mapSession(projection.session);
  const canReadEvidence = session.capabilities.includes("evidence.read");
  const crews = arrayValue(
    projection.crews,
    "Projection Crews",
    (crew, label) => mapCrew(crew, label, canReadEvidence),
  );
  const crewIds = new Set(crews.map((crew) => crew.id));
  const crewsById = new Map(crews.map((crew) => [crew.id, crew]));
  const attention = arrayValue(projection.attention, "Projection attention", mapAttention);
  if (crewIds.size !== crews.length || attention.some((item) => !crewIds.has(item.crewId))) {
    throw new PortalApiError("Projection Crew and attention identity containment is invalid.");
  }
  if (attention.some((item) => {
    if (!item.workItem) return false;
    const crew = crewsById.get(item.crewId);
    const boardItem = crew?.board.find((candidate) => candidate.cardId === item.workItem?.cardId);
    return !crew
      || item.workItem.crewId !== crew.id
      || item.workItem.boardId !== crew.boardId
      || boardItem?.goalId !== item.workItem.goalId;
  })) {
    throw new PortalApiError("Projection attention escaped its exact Crew Board WorkItem identity.");
  }
  return {
    session,
    crews,
    attention,
  };
}

export function parseProjectionEnvelope(value: unknown): ProjectionEnvelope {
  const envelope = record(value, "Projection envelope");
  if (envelope.schemaVersion !== "forge-remote.projection.v3") throw new PortalApiError("Unsupported projection schema.");
  const parsed: ProjectionEnvelope = {
    schemaVersion: "forge-remote.projection.v3",
    cursor: identifier(envelope.cursor, "Projection cursor"),
    authorityEpoch: integer(envelope.authorityEpoch, "Projection authority epoch", 1),
    cursorRevision: integer(envelope.cursorRevision, "Projection cursor revision", 0),
    cursorReset: booleanValue(envelope.cursorReset, "Projection cursor reset"),
    historyTruncated: booleanValue(envelope.historyTruncated, "Projection history marker"),
    projection: mapProjection(envelope.projection),
  };
  if (parsed.historyTruncated && !parsed.cursorReset) {
    throw new PortalApiError("A truncated projection history must carry a cursor reset.");
  }
  if (parsed.projection.session.projection.cursor !== parsed.cursor) throw new PortalApiError("Projection cursor mismatch.");
  return parsed;
}

export function parseActivationPreview(value: unknown): ActivationPreview {
  const preview = record(value, "Activation preview");
  if (preview.schemaVersion !== "forge-remote.activation-preview.v1") throw new PortalApiError("Unsupported activation preview schema.");
  return {
    schemaVersion: "forge-remote.activation-preview.v1",
    activationId: identifier(preview.activationId, "Activation ID"),
    hostLabel: identifier(preview.hostLabel, "Activation host label"),
    expiresAt: timestamp(preview.expiresAt, "Activation expiry"),
    capabilities: enumArray(preview.capabilities, capabilities, "Activation capabilities"),
    crews: arrayValue(preview.crews, "Activation Crews", (value, label) => {
      const crew = record(value, label);
      return {
        id: identifier(crew.id, `${label} ID`),
        name: identifier(crew.name, `${label} name`),
        state: identifier(crew.state, `${label} state`),
      };
    }),
    source: "portal",
    commandAuthority: enumValue(preview.commandAuthority, commandAuthorityStates, "Activation command authority"),
  };
}

export function parseActivationRedemption(value: unknown): ActivationRedemption {
  const redemption = record(value, "Activation redemption");
  if (redemption.schemaVersion !== "forge-remote.activation-redemption.v1") throw new PortalApiError("Unsupported activation redemption schema.");
  return {
    schemaVersion: "forge-remote.activation-redemption.v1",
    sessionId: identifier(redemption.sessionId, "Session ID"),
    expiresAt: timestamp(redemption.expiresAt, "Session expiry"),
  };
}
